import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import {
  startDeleteFlowEditorConfig,
  startDeleteFlowManagerConfigsByUserModule,
  startDeleteFlowValidatorConfig,
  startLoadingFlowModuleConfigs,
  startLoadingFlowRoleUsers,
  startRegisterFlowEditorConfig,
  startRegisterFlowValidatorConfig,
  startSyncFlowManagerConfigs,
  startUpdateFlowEditorConfig,
  startUpdateFlowValidatorConfig
} from "../../app/actions/flowModuleConfigActions";
import { startLoadingCatalogCiiusByLevel1 } from "../../app/actions/catalogCiiuActions";
import { startLoadingCatalogModules } from "../../app/actions/catalogModuleActions";
import { getCatalogModuleOptionLabel } from "../../app/helpers/flowEngineConfigHelper";
import { getModuleWorkAxis } from "../../app/helpers/flowCoverageAxisHelper";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import FlowCoverageWorkAreaPanel from "./FlowCoverageWorkAreaPanel";
import FlowModuleConfigTreePanel from "./FlowModuleConfigTreePanel";
import FlowModuleConfigDiagramPanel from "./FlowModuleConfigDiagramPanel";
import { Link } from "react-router-dom";

const TABS = [
  { id: "feeder", labelKey: "admin.flowModule.tab.feeder", fallback: "Alimentadores", roleKey: "feeder" },
  { id: "pre-manager", labelKey: "admin.flowModule.tab.pre-manager", fallback: "Pre-Gestores", roleKey: "pre-manager" },
  { id: "manager", labelKey: "admin.flowModule.tab.manager", fallback: "Gestores", roleKey: "manager" },
  { id: "validator", labelKey: "admin.flowModule.tab.validator", fallback: "Validadores", roleKey: "validator" },
  { id: "editor", labelKey: "admin.flowModule.tab.editor", fallback: "Editores", roleKey: "editor" },
  { id: "tree", labelKey: "admin.flowModule.tab.tree", fallback: "Árbol" },
  { id: "diagram", labelKey: "admin.flowModule.tab.diagram", fallback: "Diagrama" }
];

const COVERAGE_TAB_IDS = ["feeder", "pre-manager", "manager", "validator", "editor"];

const AdminFlowModuleConfigScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loading, managerConfigs, validatorConfigs, editorConfigs } = useSelector((s) => s.flowModuleConfig);
  const { listCatalogCiiusLevel1 = [] } = useSelector((s) => s.catalogCiiu);
  const { catalogModules = [] } = useSelector((s) => s.catalogModule);

  const [activeTab, setActiveTab] = useState("pre-manager");
  const [moduleFilter, setModuleFilter] = useState("");
  const [roleUsers, setRoleUsers] = useState({ manager: [], validator: [], editor: [], preManager: [] });
  const [treeEditorId, setTreeEditorId] = useState("");
  const [editingValidatorId, setEditingValidatorId] = useState(null);
  const [editingEditorId, setEditingEditorId] = useState(null);
  const [editingManagerKey, setEditingManagerKey] = useState(null);

  const [managerForm, setManagerForm] = useState({
    user_id: "",
    catalogmodule_id: "",
    catalogciiu_ids: [],
    observation: ""
  });
  const [validatorForm, setValidatorForm] = useState({ user_id: "", catalogmodule_id: "", manager_user_ids: [], observation: "" });
  const [editorForm, setEditorForm] = useState({ user_id: "", catalogmodule_id: "", validator_user_ids: [], observation: "" });

  const apiFilters = useMemo(
    () => (moduleFilter ? { catalogmodule_id: moduleFilter } : {}),
    [moduleFilter]
  );

  const managerFormAxis = getModuleWorkAxis(managerForm.catalogmodule_id);
  const showLegacyCiiu = managerFormAxis === "ciiu_l1";
  const activeCoverageRole = TABS.find((tab) => tab.id === activeTab)?.roleKey || "";

  useEffect(() => {
    dispatch(startLoadingFlowModuleConfigs(apiFilters));
    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingCatalogModules());
  }, [dispatch, apiFilters]);

  useEffect(() => {
    let cancelled = false;

    const loadRoleUsers = async () => {
      const moduleId = moduleFilter || '';
      const [manager, validator, editor, preManager] = await Promise.all([
        dispatch(startLoadingFlowRoleUsers('manager', moduleId)),
        dispatch(startLoadingFlowRoleUsers('validator', moduleId)),
        dispatch(startLoadingFlowRoleUsers('editor', moduleId)),
        dispatch(startLoadingFlowRoleUsers('pre-manager', moduleId))
      ]);

      if (!cancelled) {
        setRoleUsers({ manager, validator, editor, preManager });
      }
    };

    loadRoleUsers();
    return () => { cancelled = true; };
  }, [dispatch, moduleFilter]);

  useEffect(() => {
    if (moduleFilter) {
      setManagerForm((prev) => ({
        ...prev,
        catalogmodule_id: moduleFilter,
        catalogciiu_ids: [],
        observation: ""
      }));
      setEditingManagerKey(null);
      setValidatorForm((prev) => ({ ...prev, catalogmodule_id: moduleFilter }));
      setEditorForm((prev) => ({ ...prev, catalogmodule_id: moduleFilter }));
    }
  }, [moduleFilter]);

  const level1Ciius = useMemo(
    () => (listCatalogCiiusLevel1 || []).filter((item) => !item.deleted),
    [listCatalogCiiusLevel1]
  );

  const formatFlowUserLabel = (user) => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    if (user.user_name && fullName) {
      return `${user.user_name} — ${fullName} (${user.email})`;
    }
    return `${user.user_name || fullName || user.email} (${user.email})`;
  };

  const managerUsers = roleUsers.manager;
  const validatorUsers = roleUsers.validator;
  const editorUsers = roleUsers.editor;
  const preManagerUsers = roleUsers.preManager || [];
  const treeEditorUsers = useMemo(() => {
    const byId = new Map((editorUsers || []).map((user) => [Number(user.id), user]));
    (editorConfigs || []).forEach((row) => {
      const id = Number(row.user_id);
      if (id && !byId.has(id)) {
        byId.set(id, row.User_To_FlowEditorConfig || { id, user_name: String(id) });
      }
    });
    return Array.from(byId.values());
  }, [editorUsers, editorConfigs]);

  const userNameById = (id, pool = []) => {
    const match = (pool || []).find((item) => Number(item.id) === Number(id));
    return match?.user_name || String(id);
  };

  const formatValidatorManagers = (row) => {
    const names = (row.Managers_To_FlowValidatorConfig || [])
      .map((item) => item.ManagerUser_To_FlowValidatorConfigManager?.user_name
        || userNameById(item.manager_user_id, managerUsers))
      .filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  const formatEditorValidators = (row) => {
    const names = (row.Validators_To_FlowEditorConfig || [])
      .map((item) => item.ValidatorUser_To_FlowEditorConfigValidator?.user_name
        || userNameById(item.validator_user_id, validatorUsers))
      .filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  const toggleMulti = (field, value, form, setter) => {
    const current = form[field] || [];
    const exists = current.some((item) => Number(item) === Number(value) || item === value);
    const next = exists
      ? current.filter((item) => Number(item) !== Number(value) && item !== value)
      : [...current, value];
    setter({ ...form, [field]: next });
  };

  const setAllCiius = (form, setter, selected) => {
    setter({
      ...form,
      catalogciiu_ids: selected ? level1Ciius.map((item) => item.id) : []
    });
  };

  const groupedManagerConfigs = useMemo(() => {
    const groups = new Map();
    (managerConfigs || []).forEach((row) => {
      const key = `${row.user_id}:${row.catalogmodule_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          user_id: row.user_id,
          catalogmodule_id: row.catalogmodule_id,
          userName: row.User_To_FlowManagerConfig?.user_name || userNameById(row.user_id, managerUsers),
          moduleKey: row.CatalogModule_To_FlowManagerConfig?.key
            || row.CatalogModule_To_FlowManagerConfig?.process
            || String(row.catalogmodule_id),
          ciiuIds: [],
          ciiuCodes: [],
          observation: row.observation || ""
        });
      }
      const group = groups.get(key);
      if (row.catalogciiu_id && !group.ciiuIds.includes(row.catalogciiu_id)) {
        group.ciiuIds.push(row.catalogciiu_id);
        const fromRow = row.CatalogCiiu_To_FlowManagerConfig?.code;
        const fromCatalog = level1Ciius.find((item) => Number(item.id) === Number(row.catalogciiu_id));
        group.ciiuCodes.push(fromRow || fromCatalog?.code || String(row.catalogciiu_id));
      }
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      ciiuCodes: [...group.ciiuCodes].sort()
    }));
  }, [managerConfigs, managerUsers, level1Ciius]);

  const findManagerGroup = (userId, moduleId) => {
    if (!userId || !moduleId) return null;
    return groupedManagerConfigs.find((group) =>
      Number(group.user_id) === Number(userId)
      && Number(group.catalogmodule_id) === Number(moduleId)
    ) || null;
  };

  const applyManagerSelection = (userId, moduleId) => {
    const group = findManagerGroup(userId, moduleId);
    setManagerForm({
      user_id: userId ? String(userId) : "",
      catalogmodule_id: moduleId ? String(moduleId) : "",
      catalogciiu_ids: group ? [...group.ciiuIds] : [],
      observation: group?.observation || ""
    });
    setEditingManagerKey(group ? group.key : null);
  };

  const resetManagerForm = () => {
    setEditingManagerKey(null);
    setManagerForm({
      user_id: "",
      catalogmodule_id: moduleFilter || "",
      catalogciiu_ids: [],
      observation: ""
    });
  };

  const renderModuleOptions = () =>
    (catalogModules || []).filter((m) => !m.deleted).map((m) => (
      <option key={m.id} value={m.id}>{getCatalogModuleOptionLabel(m, t)}</option>
    ));

  const submitManager = async (e) => {
    e.preventDefault();
    if (!managerForm.user_id || !managerForm.catalogmodule_id || !managerForm.catalogciiu_ids.length) {
      Swal.fire(t("titles.error"), t("admin.flowModule.validation.manager"), "error");
      return;
    }
    const saved = await dispatch(startSyncFlowManagerConfigs({
      user_id: Number(managerForm.user_id),
      catalogmodule_id: Number(managerForm.catalogmodule_id),
      catalogciiu_ids: managerForm.catalogciiu_ids.map(Number),
      observation: managerForm.observation || ""
    }, apiFilters));
    if (saved) {
      resetManagerForm();
    }
  };

  const loadManagerForEdit = (group) => {
    applyManagerSelection(group.user_id, group.catalogmodule_id);
    setActiveTab("manager");
  };

  const deleteManagerGroup = async (group) => {
    const confirmed = await Swal.fire({
      title: t("admin.flowModule.deleteManagerTitle"),
      text: t("admin.flowModule.deleteManagerText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("buttons.delete"),
      cancelButtonText: t("buttons.cancel")
    });
    if (!confirmed.isConfirmed) return;
    await dispatch(startDeleteFlowManagerConfigsByUserModule(group.user_id, group.catalogmodule_id, apiFilters));
    if (editingManagerKey === group.key) {
      resetManagerForm();
    }
  };

  const submitValidator = async (e) => {
    e.preventDefault();
    if (!validatorForm.user_id || !validatorForm.catalogmodule_id) {
      Swal.fire(t("titles.error"), t("admin.flowModule.validation.validator"), "error");
      return;
    }
    const payload = {
      user_id: Number(validatorForm.user_id),
      catalogmodule_id: Number(validatorForm.catalogmodule_id),
      manager_user_ids: validatorForm.manager_user_ids.map(Number),
      observation: validatorForm.observation || ""
    };
    if (editingValidatorId) {
      await dispatch(startUpdateFlowValidatorConfig(editingValidatorId, payload, apiFilters));
      setEditingValidatorId(null);
    } else {
      await dispatch(startRegisterFlowValidatorConfig(payload));
      dispatch(startLoadingFlowModuleConfigs(apiFilters));
    }
    setValidatorForm({ user_id: "", catalogmodule_id: moduleFilter || "", manager_user_ids: [], observation: "" });
  };

  const submitEditor = async (e) => {
    e.preventDefault();
    if (!editorForm.user_id || !editorForm.catalogmodule_id) {
      Swal.fire(t("titles.error"), t("admin.flowModule.validation.editor"), "error");
      return;
    }
    const payload = {
      user_id: Number(editorForm.user_id),
      catalogmodule_id: Number(editorForm.catalogmodule_id),
      validator_user_ids: editorForm.validator_user_ids.map(Number),
      observation: editorForm.observation || ""
    };
    if (editingEditorId) {
      await dispatch(startUpdateFlowEditorConfig(editingEditorId, payload, apiFilters));
      setEditingEditorId(null);
    } else {
      await dispatch(startRegisterFlowEditorConfig(payload));
      dispatch(startLoadingFlowModuleConfigs(apiFilters));
    }
    setEditorForm({ user_id: "", catalogmodule_id: moduleFilter || "", validator_user_ids: [], observation: "" });
  };

  const loadValidatorForEdit = (row) => {
    setEditingValidatorId(row.id);
    setValidatorForm({
      user_id: String(row.user_id),
      catalogmodule_id: String(row.catalogmodule_id),
      manager_user_ids: (row.Managers_To_FlowValidatorConfig || []).map((m) => m.manager_user_id),
      observation: row.observation || ""
    });
    setActiveTab("validator");
  };

  const loadEditorForEdit = (row) => {
    setEditingEditorId(row.id);
    setEditorForm({
      user_id: String(row.user_id),
      catalogmodule_id: String(row.catalogmodule_id),
      validator_user_ids: (row.Validators_To_FlowEditorConfig || []).map((v) => v.validator_user_id),
      observation: row.observation || ""
    });
    setActiveTab("editor");
  };

  return (
    <FlowEngineGlobalGate>
    <div className={`py-4 ${activeTab === "diagram" ? "container-fluid px-4" : "container"}`}>
      <h2>{t("admin.flowModule.title")}</h2>
      <p className="text-muted">{t("admin.flowModule.subtitle")}</p>

      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">{t("admin.flowModule.filterModule")}</label>
          <select className="form-select" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
            <option value="">{t("admin.flowModule.selectModuleFirst")}</option>
            {renderModuleOptions()}
          </select>
        </div>
      </div>

      <div className="alert alert-info small">
        {t("admin.flowModule.coverageNote")}
        {" "}
        <Link to="/admin-flow-assignment-policies">{t("admin.flowModule.policiesLink")}</Link>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button type="button" className={`nav-link ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)}>
              {t(tab.labelKey, tab.fallback)}
            </button>
          </li>
        ))}
      </ul>

      {loading && !COVERAGE_TAB_IDS.includes(activeTab) && (
        <p>{t("labels.loading", "Cargando...")}</p>
      )}

      {COVERAGE_TAB_IDS.includes(activeTab) && (
        <FlowCoverageWorkAreaPanel
          catalogmoduleId={moduleFilter}
          roleKey={activeCoverageRole}
        />
      )}

      {activeTab === "feeder" && (
        <p className="text-muted small">
          {t(
            "admin.flowModule.feederHint",
            "El Alimentador tiene dos agrupadores: el 1 cubre los cuatro primeros módulos y el 2 los cuatro siguientes. Aquí se agregan las personas y el orden secuencial de cada agrupador."
          )}
        </p>
      )}

      {activeTab === "pre-manager" && (
        <p className="text-muted small">{t("admin.flowModule.preHint")}</p>
      )}

      {activeTab === "manager" && (
        <details className="mb-4">
          <summary className="mb-3">{t("admin.flowModule.legacyLinearTitle")}</summary>
          <form className="mb-4" onSubmit={submitManager}>
            <div className="row g-2 mb-2">
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={managerForm.user_id}
                  onChange={(e) => applyManagerSelection(e.target.value, managerForm.catalogmodule_id)}
                >
                  <option value="">{t("admin.flowModule.selectManager")}</option>
                  {managerUsers.map((u) => <option key={u.id} value={u.id}>{formatFlowUserLabel(u)}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={managerForm.catalogmodule_id}
                  onChange={(e) => applyManagerSelection(managerForm.user_id, e.target.value)}
                >
                  <option value="">{t("admin.flowModule.module")}</option>
                  {renderModuleOptions()}
                </select>
              </div>
              <div className="col-md-3">
                <button type="submit" className="btn btn-primary w-100">
                  {editingManagerKey ? t("buttons.update", "Actualizar") : t("buttons.add")}
                </button>
              </div>
              {editingManagerKey && (
                <div className="col-md-3">
                  <button type="button" className="btn btn-secondary w-100" onClick={resetManagerForm}>
                    {t("buttons.cancel", "Cancelar")}
                  </button>
                </div>
              )}
            </div>
            {showLegacyCiiu ? (
              <div className="mb-2">
                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                  <span className="form-label mb-0">{t("admin.flowModule.legacyCiiuTitle")}</span>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setAllCiius(managerForm, setManagerForm, true)}>
                    {t("admin.flowModule.selectAll")}
                  </button>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setAllCiius(managerForm, setManagerForm, false)}>
                    {t("admin.flowModule.selectNone")}
                  </button>
                </div>
                <p className="text-muted small mb-2">{t("admin.flowModule.legacyCiiuHint")}</p>
                <div className="d-flex flex-wrap gap-2">
                  {level1Ciius.map((c) => (
                    <label key={c.id} className="form-check-label border rounded px-2">
                      <input
                        type="checkbox"
                        className="form-check-input me-1"
                        checked={managerForm.catalogciiu_ids.some((id) => Number(id) === Number(c.id))}
                        onChange={() => toggleMulti("catalogciiu_ids", c.id, managerForm, setManagerForm)}
                      />
                      {c.code}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted small mb-2">{t("admin.flowModule.noCiiuForModule")}</p>
            )}
          </form>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>{t("admin.flowModule.colManager")}</th>
                <th>{t("admin.flowModule.module")}</th>
                <th>{t("admin.flowModule.colCiius")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupedManagerConfigs.map((group) => (
                <tr key={group.key}>
                  <td>{group.userName}</td>
                  <td>{group.moduleKey}</td>
                  <td>{group.ciiuCodes.join(", ") || "—"}</td>
                  <td className="text-nowrap">
                    <button type="button" className="btn btn-link p-0 me-2" onClick={() => loadManagerForEdit(group)}>
                      {t("buttons.edit", "Editar")}
                    </button>
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => deleteManagerGroup(group)}>
                      {t("buttons.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {activeTab === "validator" && (
        <details className="mb-4">
          <summary className="mb-3">{t("admin.flowModule.legacyLinearTitle")}</summary>
          <form className="mb-4" onSubmit={submitValidator}>
            <div className="row g-2 mb-2">
              <div className="col-md-3">
                <select className="form-select" value={validatorForm.user_id} onChange={(e) => setValidatorForm({ ...validatorForm, user_id: e.target.value })}>
                  <option value="">{t("admin.flowModule.selectValidator")}</option>
                  {validatorUsers.map((u) => <option key={u.id} value={u.id}>{formatFlowUserLabel(u)}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select" value={validatorForm.catalogmodule_id} onChange={(e) => setValidatorForm({ ...validatorForm, catalogmodule_id: e.target.value })}>
                  <option value="">{t("admin.flowModule.module")}</option>
                  {renderModuleOptions()}
                </select>
              </div>
              <div className="col-md-3">
                <button type="submit" className="btn btn-primary w-100">
                  {editingValidatorId ? t("buttons.update", "Actualizar") : t("buttons.add")}
                </button>
              </div>
              {editingValidatorId && (
                <div className="col-md-3">
                  <button type="button" className="btn btn-secondary w-100" onClick={() => { setEditingValidatorId(null); setValidatorForm({ user_id: "", catalogmodule_id: moduleFilter || "", manager_user_ids: [], observation: "" }); }}>
                    {t("buttons.cancel", "Cancelar")}
                  </button>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-md-12">
                <label className="form-label">{t("admin.flowModule.linkedManagers")}</label>
                <div className="d-flex flex-wrap gap-2">
                  {managerUsers.map((u) => (
                    <label key={u.id} className="form-check-label border rounded px-2">
                      <input type="checkbox" className="form-check-input me-1" checked={validatorForm.manager_user_ids.includes(u.id)} onChange={() => toggleMulti("manager_user_ids", u.id, validatorForm, setValidatorForm)} />
                      {u.user_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </form>
          <table className="table table-sm table-striped">
            <thead><tr><th>{t("admin.flowModule.selectValidator")}</th><th>{t("admin.flowModule.module")}</th><th>{t("admin.flowModule.linkedManagers")}</th><th></th></tr></thead>
            <tbody>
              {validatorConfigs.map((row) => (
                <tr key={row.id}>
                  <td>{row.User_To_FlowValidatorConfig?.user_name}</td>
                  <td>{row.CatalogModule_To_FlowValidatorConfig?.key || row.CatalogModule_To_FlowValidatorConfig?.process}</td>
                  <td>{formatValidatorManagers(row)}</td>
                  <td className="text-nowrap">
                    <button type="button" className="btn btn-link p-0 me-2" onClick={() => loadValidatorForEdit(row)}>{t("buttons.edit", "Editar")}</button>
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => dispatch(startDeleteFlowValidatorConfig(row.id, apiFilters))}>{t("buttons.delete")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {activeTab === "editor" && (
        <details className="mb-4">
          <summary className="mb-3">{t("admin.flowModule.legacyLinearTitle")}</summary>
          <form className="mb-4" onSubmit={submitEditor}>
            <div className="row g-2 mb-2">
              <div className="col-md-3">
                <select className="form-select" value={editorForm.user_id} onChange={(e) => setEditorForm({ ...editorForm, user_id: e.target.value })}>
                  <option value="">{t("admin.flowModule.selectEditor")}</option>
                  {editorUsers.map((u) => <option key={u.id} value={u.id}>{formatFlowUserLabel(u)}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select" value={editorForm.catalogmodule_id} onChange={(e) => setEditorForm({ ...editorForm, catalogmodule_id: e.target.value })}>
                  <option value="">{t("admin.flowModule.module")}</option>
                  {renderModuleOptions()}
                </select>
              </div>
              <div className="col-md-3">
                <button type="submit" className="btn btn-primary w-100">
                  {editingEditorId ? t("buttons.update", "Actualizar") : t("buttons.add")}
                </button>
              </div>
              {editingEditorId && (
                <div className="col-md-3">
                  <button type="button" className="btn btn-secondary w-100" onClick={() => { setEditingEditorId(null); setEditorForm({ user_id: "", catalogmodule_id: moduleFilter || "", validator_user_ids: [], observation: "" }); }}>
                    {t("buttons.cancel", "Cancelar")}
                  </button>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-md-12">
                <label className="form-label">{t("admin.flowModule.linkedValidators")}</label>
                <div className="d-flex flex-wrap gap-2">
                  {validatorUsers.map((u) => (
                    <label key={u.id} className="form-check-label border rounded px-2">
                      <input type="checkbox" className="form-check-input me-1" checked={editorForm.validator_user_ids.includes(u.id)} onChange={() => toggleMulti("validator_user_ids", u.id, editorForm, setEditorForm)} />
                      {u.user_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </form>
          <table className="table table-sm table-striped">
            <thead><tr><th>{t("admin.flowModule.selectEditor")}</th><th>{t("admin.flowModule.module")}</th><th>{t("admin.flowModule.linkedValidators")}</th><th></th></tr></thead>
            <tbody>
              {editorConfigs.map((row) => (
                <tr key={row.id}>
                  <td>{row.User_To_FlowEditorConfig?.user_name}</td>
                  <td>{row.CatalogModule_To_FlowEditorConfig?.key || row.CatalogModule_To_FlowEditorConfig?.process}</td>
                  <td>{formatEditorValidators(row)}</td>
                  <td className="text-nowrap">
                    <button type="button" className="btn btn-link p-0 me-2" onClick={() => loadEditorForEdit(row)}>{t("buttons.edit", "Editar")}</button>
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => dispatch(startDeleteFlowEditorConfig(row.id, apiFilters))}>{t("buttons.delete")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {activeTab === "tree" && (
        <FlowModuleConfigTreePanel
          editorUserId={treeEditorId}
          onEditorUserIdChange={setTreeEditorId}
          editorUsers={treeEditorUsers}
          formatFlowUserLabel={formatFlowUserLabel}
          editorConfigs={editorConfigs}
          validatorConfigs={validatorConfigs}
          managerConfigs={managerConfigs}
          validatorUsers={validatorUsers}
          managerUsers={managerUsers}
          preUsers={preManagerUsers}
          catalogModules={catalogModules}
        />
      )}

      {activeTab === "diagram" && (
        <FlowModuleConfigDiagramPanel
          catalogmoduleId={moduleFilter}
          roleUsers={{
            preManager: preManagerUsers,
            manager: managerUsers,
            validator: validatorUsers,
            editor: editorUsers
          }}
        />
      )}
    </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowModuleConfigScreen;
